import { useEffect, useRef, useState, useMemo } from 'react';

import { useLocation } from 'react-router-dom';
import styled from '@emotion/styled/macro';
import { FormattedMessage } from 'react-intl';
import { Flex, Box, Button, HStack, Heading, Text, useDisclosure } from '@chakra-ui/react';

import ConfiguratorSection from '../components/basis/ConfiguratorSection';
import ConfiguratorNavigation from '../components/basis/ConfiguratorNavigation';
import AddForwardCurveModal from '../components/basis/AddForwardCurveModal';

import AddItemButton from '../components/utils/AddItemButton';

import { ReactComponent as AddIcon } from '../icons/add.svg';

const BasisConfigurator = () => {
    const location = useLocation();

    const peakPeriodRef = useRef();
    const modelDefinitionRef = useRef();
    const forwardPriceRef = useRef();
    const spotPriceRef = useRef();
    const scalerRef = useRef();
    const dartRef = useRef();
    const optionalDataRef = useRef();

    const [activeLinkId, setActiveLinkId] = useState('');

    const addForwardCurveAndPriceModal = useDisclosure();

    const configuratorSections = useMemo(
        () => [
            {
                id: 'peak-period',
                ref: peakPeriodRef,
                title: <FormattedMessage id="basis_configurator_peak_period" />,
            },
            {
                id: 'model-definition',
                ref: modelDefinitionRef,
                title: <FormattedMessage id="basis_configurator_model_definition" />,
            },
            {
                id: 'forward-price',
                ref: forwardPriceRef,
                title: <FormattedMessage id="basis_configurator_forward_price" />,
            },
            {
                id: 'spot-price',
                ref: spotPriceRef,
                title: <FormattedMessage id="basis_configurator_spot_price" />,
            },
            {
                id: 'scaler',
                ref: scalerRef,
                title: <FormattedMessage id="basis_configurator_scaler" />,
            },
            {
                id: 'dart',
                ref: dartRef,
                title: <FormattedMessage id="basis_configurator_dart" />,
            },
            {
                id: 'optional-data',
                ref: optionalDataRef,
                title: <FormattedMessage id="basis_configurator_optional_data" />,
            },
        ],
        []
    );

    // scroll to hash if reload has occurred
    useEffect(() => {
        const { hash } = location;
        if (hash) {
            const id = hash.replace('#', '');
            const section = configuratorSections.find((el) => el.id === id).ref;
            if (section && section.current) {
                section.current.scrollIntoView();
            }
        }
    }, [configuratorSections, location]);

    return (
        <>
            <NavigationWrapper>
                <ConfiguratorNavigation configuratorSections={configuratorSections} activeLinkId={activeLinkId} />
            </NavigationWrapper>

            <MainWrapper>
                <Flex py={2} background="gray.50" justifyContent="center">
                    <CenteredFlex>
                        <ConfiguratorSection
                            id="peak-period"
                            ref={peakPeriodRef}
                            title={
                                <Heading variant="h2" my={6} textTransform="capitalize">
                                    <FormattedMessage id="basis_configurator_peak_period" />
                                </Heading>
                            }
                            activeLinkId={activeLinkId}
                            onActiveLinkIdChange={setActiveLinkId}
                        />

                        <ConfiguratorSection
                            id="model-definition"
                            ref={modelDefinitionRef}
                            title={
                                <Heading variant="h2" my={6} textTransform="capitalize">
                                    <FormattedMessage id="basis_configurator_model_definition" />
                                </Heading>
                            }
                            activeLinkId={activeLinkId}
                            onActiveLinkIdChange={setActiveLinkId}
                        />

                        <ConfiguratorSection
                            id="forward-price"
                            ref={forwardPriceRef}
                            title={
                                <Flex justify="space-between" align="center">
                                    <Heading variant="h2" my={6} textTransform="capitalize">
                                        <FormattedMessage id="basis_configurator_forward_price" />
                                    </Heading>
                                    <Box>
                                        <AddItemButton
                                            leftIcon={<AddIcon />}
                                            onClick={() => addForwardCurveAndPriceModal.onOpen()}
                                        >
                                            <Text size="sm" color="gray.900" pl={1} pt={1}>
                                                <FormattedMessage id="basis_configurator_add_new_forward_curve_and_price_item" />
                                            </Text>
                                        </AddItemButton>
                                    </Box>
                                </Flex>
                            }
                            activeLinkId={activeLinkId}
                            onActiveLinkIdChange={setActiveLinkId}
                        />

                        <ConfiguratorSection
                            id="spot-price"
                            ref={spotPriceRef}
                            title={
                                <Heading variant="h2" my={6} textTransform="capitalize">
                                    <FormattedMessage id="basis_configurator_spot_price" />
                                </Heading>
                            }
                            activeLinkId={activeLinkId}
                            onActiveLinkIdChange={setActiveLinkId}
                        />

                        <ConfiguratorSection
                            id="scaler"
                            ref={scalerRef}
                            title={
                                <Heading variant="h2" my={6} textTransform="capitalize">
                                    <FormattedMessage id="basis_configurator_scaler" />
                                </Heading>
                            }
                            activeLinkId={activeLinkId}
                            onActiveLinkIdChange={setActiveLinkId}
                        />

                        <ConfiguratorSection
                            id="dart"
                            ref={dartRef}
                            title={
                                <Heading variant="h2" my={6} textTransform="capitalize">
                                    <FormattedMessage id="basis_configurator_dart" />
                                </Heading>
                            }
                            activeLinkId={activeLinkId}
                            onActiveLinkIdChange={setActiveLinkId}
                        />

                        <ConfiguratorSection
                            id="optional-data"
                            ref={optionalDataRef}
                            title={
                                <Heading variant="h2" my={6} textTransform="capitalize">
                                    <FormattedMessage id="basis_configurator_optional_data" />
                                </Heading>
                            }
                            activeLinkId={activeLinkId}
                            onActiveLinkIdChange={setActiveLinkId}
                        />
                    </CenteredFlex>
                </Flex>

                <ActionButtons>
                    <HStack spacing={4}>
                        <Button textTransform="capitalize" onClick={() => {}} disabled>
                            <FormattedMessage id="common_cancel" />
                        </Button>
                        <Button textTransform="capitalize" onClick={() => {}} disabled>
                            <FormattedMessage id="basis_configurator_create_basis" />
                        </Button>
                    </HStack>
                </ActionButtons>
            </MainWrapper>

            {addForwardCurveAndPriceModal.isOpen && (
                <AddForwardCurveModal
                    isOpen
                    onClose={addForwardCurveAndPriceModal.onClose}
                    header={<FormattedMessage id="common_delete_modal_heading" values={{ item: 'Forward' }} />}
                    hasExtraStep
                    onConfirm={() => {
                        addForwardCurveAndPriceModal.onClose();
                    }}
                />
            )}
        </>
    );
};

const MainWrapper = styled(Box)`
    width: 100%;
    position: fixed;
    top: 150px;
    right: 0;
    bottom: 120px;
    left: 0;
    overflow: auto;
`;

const CenteredFlex = styled(Flex)`
    margin: 0 20px;
    width: 100%;
    max-width: 1200px;
    flex-direction: column;
`;

const NavigationWrapper = styled.div`
    width: 100%;
    position: fixed;
    top: 81px;
`;

const ActionButtons = styled(Flex)`
    position: fixed;
    bottom: 40px;
    width: 100%;
    height: 80px;
    padding: 0 16px;
    align-items: center;
    justify-content: flex-end;
    background: var(--chakra-colors-white);
    border-top: 1px solid var(--chakra-colors-gray-200);
`;

export default BasisConfigurator;
